import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse } from "types/ApiResponse";

import { downloadInvoice, getBillings } from "./thunk";
import { BillingInvoice, BillingInvoicesPayload } from "types/biling.api";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface BilingInvoiceStatus {
  fetch: RequestStatus;
  download: RequestStatus;
}

interface BilingInvoiceErrors {
  fetch: string | null;
  download: string | null;
}

export interface BilingInvoiceState {
  bilingInvoices: BillingInvoice[];
  downloadedInvoice: string | null;
  downloadingUrls: string[];
  pageHistory: (string | null)[];
  next_pageUrl: string | null;
  status: BilingInvoiceStatus;
  errors: BilingInvoiceErrors;
}

export const initialState: BilingInvoiceState = {
  bilingInvoices: [],
  downloadedInvoice: "",
  downloadingUrls: [],
  next_pageUrl: null,
  pageHistory: [null],
  status: {
    fetch: "idle",
    download: "idle",
  },
  errors: {
    fetch: null,
    download: null,
  },
};

const billingInvoiceSlice = createSlice({
  name: "billingInvoice",
  initialState,
  reducers: {
    clearDownloadedInvoice: (state) => {
      state.downloadedInvoice = null;
      state.status.download = "idle";
    },
  },
  extraReducers: (builder) => {
    // get bililings
    builder.addCase(getBillings.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getBillings.fulfilled,
      (state, action: PayloadAction<ApiResponse<BillingInvoicesPayload>>) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.errors.fetch = null;
        state.bilingInvoices = action.payload.payload.items;
        state.next_pageUrl = action.payload.payload.next_page;
        state.pageHistory.push(state.next_pageUrl);
      }
    );
    builder.addCase(getBillings.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    builder.addCase(downloadInvoice.pending, (state, action) => {
      state.status.download = "loading";
      state.errors.download = null;
      // Initialize downloadingUrls if undefined (for persisted state compatibility)
      if (!state.downloadingUrls) {
        state.downloadingUrls = [];
      }
      const invoiceUrl = action.meta.arg.invoiceUrl;
      if (!state.downloadingUrls.includes(invoiceUrl)) {
        state.downloadingUrls.push(invoiceUrl);
      }
    });

    builder.addCase(downloadInvoice.fulfilled, (state, action) => {
      state.status.download = "succeeded";
      state.errors.download = null;
      const url = window.URL.createObjectURL(action.payload);
      state.downloadedInvoice = url;
      const invoiceUrl = action.meta.arg.invoiceUrl;
      // Initialize downloadingUrls if undefined (for persisted state compatibility)
      if (!state.downloadingUrls) {
        state.downloadingUrls = [];
      }
      state.downloadingUrls = state.downloadingUrls.filter(
        (downloadUrl) => downloadUrl !== invoiceUrl
      );
    });
    builder.addCase(downloadInvoice.rejected, (state, action) => {
      state.status.download = "failed";
      state.errors.download = action.payload || null;
      const invoiceUrl = action.meta.arg.invoiceUrl;
      // Initialize downloadingUrls if undefined (for persisted state compatibility)
      if (!state.downloadingUrls) {
        state.downloadingUrls = [];
      }
      state.downloadingUrls = state.downloadingUrls.filter(
        (downloadUrl) => downloadUrl !== invoiceUrl
      );
    });
  },
});

export const { clearDownloadedInvoice } = billingInvoiceSlice.actions;

export default billingInvoiceSlice.reducer;

export const selectBillingInvoiceState = (state: any) =>
  state.billing as typeof initialState;
